import { weatherParameterFieldDisplayName } from "@/i18n/weather-parameter-fields";
import { useValueNameCreator } from "@/overlay/components/SmartForm/SmartFormUtils/FormHooks/useFormInstructions";
import type { InputInstruction, PathOrUndefined } from "@/overlay/components/SmartForm/SmartFormUtils/commonTypeUtils";
import { FormField } from "@/overlay/components/form-field";
import type { ParameterRange } from "@mm/api-layers.meteomatics.com";
import * as weatherParamUtil from "@mm/weather-parameter-utils";
import type {
  FieldNames,
  HasLayerIdxNarrowing,
  PartialWeatherParameter,
  WeatherParamFormatOption,
} from "@mm/weather-parameter-utils";
import { type ChangeEvent, Fragment, useEffect, useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

/**
 * Convert the weather option object to an array for rendering purpose.
 */
function flattenOptions(
  optionByName: WeatherParamFormatOption,
): [FieldNames, string[] | ParameterRange | ParameterRange[]][] {
  // TODO: This returns new array which can trigger re-rendering
  const allFields: [FieldNames, string[] | ParameterRange | ParameterRange[]][] = Object.entries(
    optionByName,
  ) as unknown as any;
  return allFields;
}

/**
 * Shape of the data the input component will send on the form hook.
 */
export type ParamOptionInputData = Partial<HasLayerIdxNarrowing<PartialWeatherParameter>>;
type ParamOptionFieldPath = PathOrUndefined<ParamOptionInputData>;

type ParamOptionInputProps = {
  instruction: InputInstruction<{
    parameter: HasLayerIdxNarrowing<PartialWeatherParameter>;
  }>;
  isCreationMode?: boolean;
};

function clampRangeValue(range: ParameterRange, value: number) {
  return Math.min(range.stop, Math.max(range.start, value));
}

function isRangeLike(value: unknown): value is ParameterRange {
  return (
    !!value && typeof value === "object" && "start" in value && typeof (value as ParameterRange).start === "number"
  );
}

function isStringArrayLike(value: unknown): value is string[] {
  return Array.isArray(value) && (value.length === 0 || typeof value[0] === "string");
}

function getFieldRanges(fieldDesc: string[] | ParameterRange | ParameterRange[]) {
  if (isRangeLike(fieldDesc)) {
    return [fieldDesc];
  }

  if (Array.isArray(fieldDesc) && (fieldDesc.length === 0 || isRangeLike(fieldDesc[0]))) {
    return fieldDesc as ParameterRange[];
  }

  return [];
}

function getActiveRange(ranges: ParameterRange[], value: string | undefined): ParameterRange {
  if (value) {
    const matchingRange = ranges.find((range) => weatherParamUtil.parseParameterRange(range, value) != null);
    if (matchingRange) {
      return matchingRange;
    }
  }

  return ranges[0];
}

function getRangeNumericValue(ranges: ParameterRange[], value: string | undefined) {
  if (!value) {
    return null;
  }

  for (const range of ranges) {
    const parsed = weatherParamUtil.parseParameterRange(range, value);
    if (parsed != null) {
      return parsed;
    }
  }

  return null;
}

function RangeOptionField(props: {
  createInputName: (name?: ParamOptionFieldPath) => string;
  currOptionValue: string | undefined;
  fieldName: FieldNames;
  formMethods: UseFormReturn;
  isCreationMode?: boolean;
  ranges: ParameterRange[];
}) {
  const { createInputName, currOptionValue, fieldName, formMethods, isCreationMode, ranges } = props;
  const { register, setValue } = formMethods;

  const activeRangeFromValue = useMemo(() => getActiveRange(ranges, currOptionValue), [currOptionValue, ranges]);
  const [selectedUnit, setSelectedUnit] = useState(activeRangeFromValue.unit);

  useEffect(() => {
    setSelectedUnit(activeRangeFromValue.unit);
  }, [activeRangeFromValue.unit]);

  const selectedRange = useMemo(
    () => ranges.find((range) => range.unit === selectedUnit) ?? activeRangeFromValue,
    [activeRangeFromValue, ranges, selectedUnit],
  );
  const displayedValue = getRangeNumericValue(ranges, currOptionValue) ?? (isCreationMode ? selectedRange.start : "");

  function handleUnitChange(event: ChangeEvent<HTMLSelectElement>) {
    const nextUnit = event.target.value;
    const nextRange = ranges.find((range) => range.unit === nextUnit);
    if (!nextRange) {
      return;
    }

    const nextValue = clampRangeValue(nextRange, getRangeNumericValue(ranges, currOptionValue) ?? nextRange.start);
    setSelectedUnit(nextUnit);
    setValue(
      createInputName(`narrowed_selection.fields.${fieldName}`),
      weatherParamUtil.formatParameterRange(nextRange, nextValue),
    );
  }

  return (
    <FormField label={weatherParameterFieldDisplayName[fieldName]()}>
      <div className="grid grid--center-horizontal" data-columns="1,flex">
        <input
          data-testid="range-input"
          type="number"
          min={selectedRange.start}
          max={selectedRange.stop}
          defaultValue={displayedValue}
          {...register(createInputName(`narrowed_selection.fields.${fieldName}`), {
            setValueAs: (num) =>
              weatherParamUtil.formatParameterRange(selectedRange, clampRangeValue(selectedRange, +num)),
          })}
        />
        {ranges.length > 1 ? (
          <select data-testid="range-unit-dropdown" value={selectedUnit} onChange={handleUnitChange}>
            {ranges.map((range) => (
              <option key={range.unit} value={range.unit}>
                {range.unit}
              </option>
            ))}
          </select>
        ) : (
          <div>{selectedRange.unit}</div>
        )}
      </div>
    </FormField>
  );
}

/**
 * Given a PartialWeatherParameter object, which has the index narrowing,
 * render all of the selected format options.
 *
 * e.g:
 * Given: HasLayerIdxNarrowing<PartialWeatherParameter> that represents "wind_speed_<level>:<unit>"
 * Render inputs for:
 * - level
 * - unit
 *
 * Note: This is a React form hook version of:
 * <ParameterFormatForm/> under
 * metx-core/src/overlay/components/WeatherParameterForm/index.tsx
 */
export function ParamOptionSelectList(props: ParamOptionInputProps) {
  const { instruction, isCreationMode } = props;
  const {
    formMethods: { register },
    currValue,
  } = instruction;
  const { createInputName } = useValueNameCreator<ParamOptionInputData>(instruction);

  const formatOptions = weatherParamUtil.getSelectedFormatOptions(currValue.parameter);
  const optionFields = flattenOptions(formatOptions);

  return (
    // Note: Providing key in Fragment here forces the component state to clear
    // when the format of the parameter changed
    // narrowed_selection.layerIdx represents which format is selected.
    <Fragment key={`${currValue.parameter.narrowed_selection.layerIdx}_${currValue.parameter.parameter.name}`}>
      {optionFields.map(([fieldName, fieldDesc], fieldIdx) => {
        const currOptionValue: string | undefined = weatherParamUtil.getSelectedFormatOptionValues(currValue.parameter)[
          fieldName
        ];
        const ranges = getFieldRanges(fieldDesc);

        if (ranges.length > 0) {
          return (
            <RangeOptionField
              key={`${fieldIdx}_${currValue.parameter.parameter.name}`}
              createInputName={createInputName}
              currOptionValue={currOptionValue}
              fieldName={fieldName}
              formMethods={instruction.formMethods}
              isCreationMode={isCreationMode}
              ranges={ranges}
            />
          );
        }

        if (!isStringArrayLike(fieldDesc)) {
          return <Fragment key={`${fieldIdx}_${currValue.parameter.parameter.name}`} />;
        }

        {
          // Set the default value to the first option if nothing is selected.
          const hasCurrValue = !!currOptionValue;
          const hasAvailableOptions = fieldDesc.length > 0;
          if (!hasAvailableOptions) {
            // If the option value list is empty, do not render the dropdown.
            return <Fragment key={`${fieldIdx}_${currValue.parameter.parameter.name}`} />;
          }
          const firstOption = fieldDesc[0];
          const _optionValue = hasCurrValue ? currOptionValue : firstOption;
          return (
            <FormField
              key={`${fieldIdx}_${currValue.parameter.parameter.name}`}
              label={weatherParameterFieldDisplayName[fieldName]()}
            >
              <select
                data-testid={"option-dropdown"}
                defaultValue={isCreationMode ? _optionValue : undefined}
                value={!isCreationMode ? _optionValue : undefined}
                {...register(createInputName(`narrowed_selection.fields.${fieldName}`))}
              >
                {fieldDesc.map((val) => {
                  return (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  );
                })}
              </select>
            </FormField>
          );
        }
      })}
    </Fragment>
  );
}
